import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment/moment'
import i18n from 'simple-react-i18n'
import { findKey, min, minBy, orderBy, partition } from 'lodash'
import ReactDOMServer from 'react-dom/server'
import { Grid } from '@mui/material'
import PluviometryAction from '../../../actions/PluviometryAction'
import PluviometerDto from '../../../dto/PluviometerDto'
import DtoPluvioMeasure from '../../../dto/measures/DtoPluvioMeasure'
import DtoPluviometerStats from '../../../dto/DtoPluviometerStats'
import ProgressCard from '../../../../components/card/ProgressCard'
import MessageCard from '../../../../components/card/MessageCard'
import Card from '../../../../components/card/Card'
import EChartV6Pluvio from '../../../../components/echart/EChartV6Pluvio'
import ChartTabsSuivi from '../../../../components/echart/ChartTabsSuivi'
import SimpleChartTabsPluvio from '../../../../components/echart/SimpleChartTabsPluvio'
import {
    exportExcelIcon,
    exportPictureIcon,
    getAxisIntervalFormatter,
} from '../../../../components/echart/EChartUtils'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { DEFROUNDVALUE, nFormatter } from '../../../../utils/NumberUtil'
import { getExportFormat } from '../../../../utils/StringUtil'
import { getFullDate, getMonthYear, getYearDate } from '../../../../utils/DateUtil'
import { statusIcon } from '../../../../utils/StatusUtil'
import { getLabel } from '../../../../utils/StoreUtils'
import { DATATYPE_RAIN, getGroupFunc } from '../../../../utils/PluviometryUtils'
import {
    AUTO,
    SUM_DAY,
    SUM_DECADE,
    SUM_HOUR,
    SUM_MONTH,
    SUM_WEEK,
    SUM_YEAR,
} from '../../../constants/PluvioOptionConstant'
import PanelEfficientRainCalcul from '../utils/PanelEfficientRainCalcul'

const DATATYPE_ETP = 3
const DATATYPE_EFFECTIVE_RAIN = -1
const COLOR_RAIN = 'blue'
const COLOR_ETP = '#009dbd'
const COLOR_EFFICIENT_POSITIVE = '#6a37ff'
const COLOR_EFFICIENT_NEGATIVE = '#ff3312'
const CHART_HEIGHT = 420

const INITIAL_DISPLAY_MODES = {
    auto: true,
    all: false,
    sumHour: false,
    sumDay: false,
    sumWeek: false,
    sumDecade: false,
    sumMonth: false,
    sumYear: false,
    personalizedGrouping: false,
}

const formatTooltipDate = (date, pluvioGroupedMode) => {
    switch (pluvioGroupedMode) {
        case SUM_HOUR:
        case SUM_DAY:
        case SUM_WEEK:
        case SUM_DECADE:
            return getFullDate(date)
        case SUM_MONTH:
            return getMonthYear(date)
        case SUM_YEAR:
            return getYearDate(date)
        default:
            return getFullDate(date)
    }
}

const buildSeries = (type, measures, infos, color) => ({
    name: infos?.label ?? '',
    type,
    barWidth: 4,
    showSymbol: false,
    symbol: 'none',
    connectNulls: false,
    itemStyle: { color },
    lineStyle: { color },
    data: measures.map(m => ({
        value: [m.date, m.value],
        rawMeasure: m,
        unit: infos?.unit ?? '',
    })),
})

const Graphic = ({
    pluviometerInfos,
    data,
    minDate,
    maxDate,
    pluvioGroupedMode,
}) => {
    const contributors = useSelector(state => state.ContributorReducer.contributors)
    const pluviometryDataTypes = useSelector(state => state.PluviometryReducer.pluviometryDataTypes)

    const [rainData, etpData, efficientData] = data
    const [efficientPositive, efficientNegative] = partition(efficientData.measures, m => m.value > 0)

    const allMinDates = data.map(d => minBy(d.measures, 'date')?.date)
    const chartMinDate = minDate || min(allMinDates)
    const axisLabelObj = getAxisIntervalFormatter(moment(maxDate), moment(minDate || chartMinDate))

    const series = [
        buildSeries('bar', rainData.measures, rainData.stat, COLOR_RAIN),
        buildSeries('line', etpData.measures, etpData.stat, COLOR_ETP),
        buildSeries('line', efficientPositive, { label: `${efficientData?.stat?.label ?? ''} >0 ` }, COLOR_EFFICIENT_POSITIVE),
        buildSeries('line', efficientNegative, { label: `${efficientData?.stat?.label ?? ''} <0 ` }, COLOR_EFFICIENT_NEGATIVE),
    ]

    const buildExportRow = (serieName, measure, roundValue) => ({
        stationCode: { value: pluviometerInfos.code },
        stationName: { value: pluviometerInfos.name },
        date: { value: getFullDate(measure.date), format: 'dd/MM/yyyy HH:mm:ss', cellType: 'date' },
        value: { value: measure.value, format: getExportFormat(roundValue), cellType: 'number' },
        type: { value: serieName },
        producer: { value: getLabel(contributors, measure.producer, 'mnemonique'), cellType: 'string' },
    })

    const getExportData = () => {
        const dataExport = series.flatMap(serie => {
            const sampleMeasure = serie.data[0]?.rawMeasure
            const roundValue = pluviometryDataTypes
                .find(pdt => pdt.id === sampleMeasure?.dataType)?.numberDecimal || DEFROUNDVALUE
            return serie.data
                .filter(d => d.value[0] >= minDate && d.value[0] <= maxDate)
                .map(d => buildExportRow(serie.name, d.rawMeasure, roundValue))
        })
        if (dataExport.length) {
            dataExport[0].headers = ['stationCode', 'stationName', 'date', 'value', 'type', 'producer']
        }
        return dataExport
    }

    const option = {
        title: {
            text: `[${pluviometerInfos.code}]${pluviometerInfos.name ? `   -   [${pluviometerInfos.name}]` : ''}`,
            left: 'center',
            top: 5,
        },
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const refDate = params[0]?.data?.rawMeasure?.date
                const date = formatTooltipDate(refDate, pluvioGroupedMode)
                const lines = orderBy(params.map(p => ({
                    seriesIndex: p.seriesIndex,
                    marker: p.marker,
                    seriesName: p.seriesName,
                    value: p.data?.rawMeasure?.value,
                    status: ReactDOMServer.renderToString(statusIcon(p.data?.rawMeasure, 20)),
                    unit: p.data?.unit ?? '',
                })), 'seriesIndex', 'asc')
                const body = lines.map(o => `<span style="display:flex;align-items:center;">${o.marker} ${o.seriesName} : ${o.value} ${o.unit}<span style="padding-left:5px">${o.status}</span></span>`).join('')
                return `${date} <br /> ${body} `
            },
        },
        grid: [{ top: 35, right: '2%', bottom: 100, left: 60 }],
        xAxis: [{
            type: 'time',
            position: 'bottom',
            min: chartMinDate,
            max: maxDate || moment().valueOf(),
            gridIndex: 0,
            interval: axisLabelObj.interval,
            axisLabel: { show: true, formatter: v => String(axisLabelObj.formatter(v)) },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
        }],
        yAxis: [{
            type: 'value',
            name: `${i18n.effectiveRain} ${data[0]?.stat?.unit ? `[${data[0]?.stat?.unit}]` : ''}`,
            nameLocation: 'middle',
            nameGap: 40,
            gridIndex: 0,
            splitLine: { show: true },
            axisLabel: { formatter: nFormatter },
        }],
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        dataZoom: [
            { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
            { type: 'slider', xAxisIndex: 0, bottom: 5, height: 18, filterMode: 'none' },
        ],
        legend: { bottom: 35, type: 'scroll' },
        toolbox: {
            show: true,
            right: 65,
            feature: {
                saveAsImage: { title: i18n.export, icon: exportPictureIcon },
                myToolExport: {
                    show: true,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => exportFile({
                        data: getExportData(),
                        exportType: 'xlsx',
                        titleFile: i18n.overview,
                    }),
                },
            },
        },
        series,
    }

    return <EChartV6Pluvio option={option} style={{ height: CHART_HEIGHT }} />
}

Graphic.propTypes = {
    pluviometerInfos: PropTypes.shape({
        code: PropTypes.string,
        name: PropTypes.string,
    }),
    data: PropTypes.arrayOf(PropTypes.shape({
        measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasure)),
        stat: PropTypes.instanceOf(DtoPluviometerStats),
    })),
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    pluvioGroupedMode: PropTypes.string,
}

const GraphEfficientRain = ({
    pluviometer,
    pluviometerStatistics,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const [displayModes, setDisplayModes] = useState(INITIAL_DISPLAY_MODES)
    const [minDate, setMinDate] = useState(undefined)
    const [maxDate, setMaxDate] = useState(moment().valueOf())

    const dispatch = useDispatch()
    const pluvioMeasures = useSelector(state => state.PluviometryReducer.pluvioMeasures)

    useEffect(() => {
        const activeMode = findKey(displayModes, mode => displayModes[mode])
        const groupModeFunc = getGroupFunc(activeMode, displayModes)
        const isShortRange = minDate && minDate > moment(maxDate || moment().valueOf()).subtract(1, 'month').valueOf()
        const defaultGroupMode = isShortRange ? 'all' : 'MAX'

        setDataLoaded(false)
        const baseInput = {
            stationId: pluviometer.id,
            groupFunc: groupModeFunc === AUTO ? defaultGroupMode : groupModeFunc,
            chartMode: false,
            startDate: undefined,
            endDate: moment().valueOf(),
        }
        const rainInput = { ...baseInput, dataType: DATATYPE_RAIN }
        const etpInput = { ...baseInput, dataType: DATATYPE_ETP }
        const efficientInput = { ...baseInput, dataType: DATATYPE_EFFECTIVE_RAIN }

        dispatch(PluviometryAction.loadPluvioChartChronicMeasures(
            [rainInput, etpInput, efficientInput],
            setProgress,
        )).then(() => setDataLoaded(true))
    }, [dispatch, displayModes, pluviometer])

    const onChangeDates = (dates, forced) => {
        if (minDate !== dates.minDate || maxDate !== dates.maxDate || forced) {
            setMinDate(dates?.minDate)
            setMaxDate(dates?.maxDate)
        }
    }

    if (pluvioMeasures.length !== 3) {
        return <ProgressCard progress={progress} />
    }

    const endDate = maxDate ?? moment().valueOf()
    const filterRange = m => m.date >= minDate && m.date <= endDate

    const data = [
        { measures: pluvioMeasures[0].measures.filter(filterRange), stat: pluviometerStatistics.find(s => s.typeId === DATATYPE_RAIN) },
        { measures: pluvioMeasures[1].measures.filter(filterRange), stat: pluviometerStatistics.find(s => s.typeId === DATATYPE_ETP) },
        { measures: pluvioMeasures[2].measures.filter(filterRange), stat: pluviometerStatistics.find(s => s.typeId === DATATYPE_EFFECTIVE_RAIN) },
    ]

    const numberMeasures = pluvioMeasures[0].measures.length
        + pluvioMeasures[1].measures.length
        + pluvioMeasures[2].measures.length
    const hasData = dataLoaded && numberMeasures > 0
    const activeMode = findKey(displayModes, mode => displayModes[mode])

    return (
        <div>
            {hasData && (
                <div style={{ paddingTop: 5, margin: '0 10' }}>
                    <PanelEfficientRainCalcul
                        allMeasures={{
                            rain: pluvioMeasures[0]?.measures,
                            ETP: pluvioMeasures[1]?.measures,
                            efficientRain: pluvioMeasures[2]?.measures,
                        }}
                    />
                </div>
            )}
            {!dataLoaded && (
                <ProgressCard progress={progress} round />
            )}
            {numberMeasures === 0 && (
                <MessageCard>{i18n.noDataToDisplay}</MessageCard>
            )}
            {hasData && (
                <Card className='card margin-1' noMargin={false} round>
                    <ChartTabsSuivi onChangeDate={onChangeDates} />
                    <Grid sx={{ top: '9px', left: '32rem', position: 'absolute', zIndex: 10 }}>
                        <SimpleChartTabsPluvio
                            displayModes={displayModes}
                            displayModesUniqChoise
                            changeParent={setDisplayModes}
                        />
                    </Grid>
                    <Grid sx={{ paddingTop: '3rem' }}>
                        <Graphic
                            pluviometerInfos={{ code: pluviometer.code, name: pluviometer.name }}
                            data={data}
                            minDate={minDate}
                            maxDate={maxDate}
                            pluvioGroupedMode={getGroupFunc(activeMode, displayModes)}
                        />
                    </Grid>
                </Card>
            )}
        </div>
    )
}

GraphEfficientRain.propTypes = {
    pluviometer: PropTypes.instanceOf(PluviometerDto),
    pluviometerStatistics: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluviometerStats)),
}

export default GraphEfficientRain
